"use strict";

const luaparse = require("../../../../vendor/luaparse/luaparse.js");

const PARSE_OPTS = {
    luaVersion: "5.1",
    locations: true,
    comments: true,
    ranges: true,
};

function primitive(name) {
    return { kind: "primitive", name: name };
}

function classType(name) {
    return { kind: "class", name: name };
}

function splitTop(str, sep) {
    const out = [];
    let buf = "";
    let depth = 0;
    for (let i = 0; i < str.length; i++) {
        const c = str[i];
        if (c === "(") depth++;
        else if (c === ")") depth--;
        if (c === sep && depth === 0) {
            out.push(buf);
            buf = "";
        } else {
            buf += c;
        }
    }
    if (buf.trim()) out.push(buf);
    return out.map((s) => s.trim()).filter(Boolean);
}

function parseFun(str) {
    const m = str.trim().match(/^fun\s*\((.*)\)(?:\s*:\s*(.*))?$/);
    if (!m) return null;
    const params = splitTop(m[1], ",").map(parseParam);
    const returns = m[2] ? splitTop(m[2], ",").map(parseType) : [];
    return { kind: "fun", params, returns };
}

function parseParam(s) {
    const m = s.trim().match(/^(\w+)\??\s*:\s*(.*)$/);
    if (m) return { name: m[1], type: parseType(m[2]) };
    const only = s.trim().match(/^(\w+)\??$/);
    if (only) return { name: only[1], type: primitive("any") };
    return { name: "arg", type: parseType(s) };
}

function parseType(str) {
    if (!str) return primitive("any");
    str = str.trim();
    const fun = parseFun(str);
    if (fun) return fun;
    const union = splitTop(str, "|");
    if (union.length > 1) {
        return { kind: "union", types: union.map(parseType) };
    }
    const prims = ["string", "number", "integer", "boolean", "any", "nil", "table"];
    if (prims.indexOf(str) >= 0) return primitive(str);
    return classType(str);
}

function consumeType(rest) {
    rest = rest.trim();
    if (rest.startsWith("fun")) {
        let depth = 0;
        let i = 0;
        let seen = false;
        for (; i < rest.length; i++) {
            if (rest[i] === "(") {
                depth++;
                seen = true;
            } else if (rest[i] === ")") {
                depth--;
                if (seen && depth === 0) {
                    i++;
                    break;
                }
            }
        }
        if (rest[i] === ":") {
            // skip return types: identifiers, commas, unions
            i++;
            while (i < rest.length && /[\w.\s|,]/.test(rest[i])) i++;
        }
        return { type: parseType(rest.slice(0, i).trim()), doc: rest.slice(i).trim() };
    }
    const m = rest.match(/^([\w.|]+)(?:\s+(.*))?$/);
    if (!m) return { type: primitive("any"), doc: rest };
    return { type: parseType(m[1]), doc: m[2] || "" };
}

function parseLuaCats(text) {
    const classes = Object.create(null);
    const globals = Object.create(null);
    const pending = [];
    const lines = text.split("\n");
    let currentClass = null;

    for (let i = 0; i < lines.length; i++) {
        const raw = lines[i];
        const line = raw.trim();
        const loc = i + 1;

        const classMatch = line.match(/^---@class\s+(\w+)/);
        if (classMatch) {
            currentClass = { name: classMatch[1], fields: Object.create(null), doc: "" };
            classes[currentClass.name] = currentClass;
            pending.push({ kind: "class", name: currentClass.name, line: loc });
            continue;
        }

        const fieldMatch = line.match(/^---@field\s+(?:(?:public|protected|private)\s+)?(\w+)\??\s+(.*)$/);
        if (fieldMatch && currentClass) {
            const consumed = consumeType(fieldMatch[2]);
            currentClass.fields[fieldMatch[1]] = {
                name: fieldMatch[1],
                type: consumed.type,
                doc: consumed.doc,
            };
            continue;
        }

        const typeMatch = line.match(/^---@type\s+(\S+(?:\s*\|\s*\S+)*)/);
        if (typeMatch) {
            currentClass = null;
            pending.push({ kind: "type", type: parseType(typeMatch[1].trim()), line: loc });
            continue;
        }

        const paramMatch = line.match(/^---@param\s+(\w+)\??\s+(.*)$/);
        if (paramMatch) {
            currentClass = null;
            const consumed = consumeType(paramMatch[2]);
            pending.push({ kind: "param", name: paramMatch[1], type: consumed.type, doc: consumed.doc, line: loc });
            continue;
        }

        const returnMatch = line.match(/^---@return\s+(.*)$/);
        if (returnMatch) {
            currentClass = null;
            const consumed = consumeType(returnMatch[1]);
            pending.push({ kind: "return", type: consumed.type, doc: consumed.doc, line: loc });
            continue;
        }

        if (line.startsWith("---")) {
            continue;
        }

        const inline = raw.match(/---@type\s+(\S+)/);
        const assign = line.match(/^([A-Za-z_]\w*)\s*=/);
        if (inline && assign) {
            globals[assign[1]] = parseType(inline[1]);
        }

        if (line.length > 0) {
            currentClass = null;
        }
    }

    return { classes, globals, pending };
}

function takePendingBefore(pending, line) {
    const taken = [];
    for (let i = pending.length - 1; i >= 0; i--) {
        const p = pending[i];
        if (p.line < line && line - p.line <= 8) {
            taken.unshift(p);
        }
    }
    return taken.filter((p) => p.kind === "type" || p.kind === "param" || p.kind === "return");
}

function parseLua(text, line, column) {
    const attempts = [text];
    if (line) {
        const lines = text.split("\n");
        const idx = line - 1;
        if (idx >= 0 && idx < lines.length) {
            const blanked = lines.slice();
            blanked[idx] = "";
            attempts.push(blanked.join("\n"));

            const col = column || (lines[idx].length + 1);
            const prefix = lines[idx].slice(0, col - 1);
            const patched = lines.slice();
            patched[idx] = "local __cursor = " + prefix.replace(/[.:]\w*$/, "").replace(/[.:]\s*$/, "") + " nil";
            attempts.push(patched.join("\n"));
        }
    }
    attempts.push(text + "\nend\nend\n");
    attempts.push(text + "\n)\nend\n");

    for (let i = 0; i < attempts.length; i++) {
        try {
            return luaparse.parse(attempts[i], PARSE_OPTS);
        } catch (e) {
            // try next salvage
        }
    }
    return { type: "Chunk", body: [], comments: [] };
}

function inferExpr(node, env) {
    if (!node) return primitive("any");
    switch (node.type) {
        case "StringLiteral":
            return primitive("string");
        case "NumericLiteral":
            return primitive("number");
        case "BooleanLiteral":
            return primitive("boolean");
        case "NilLiteral":
            return primitive("nil");
        case "Identifier":
            return lookupName(env, node.name) || primitive("any");
        case "TableConstructorExpression":
            return tableFromConstructor(node, env);
        case "MemberExpression":
        case "IndexExpression": {
            const obj = inferExpr(node.base || node.identifier, env);
            const key = node.identifier
                ? node.identifier.name || node.identifier.value
                : (node.index && node.index.value);
            return fieldType(env, obj, key) || primitive("any");
        }
        case "CallExpression":
        case "StringCallExpression":
        case "TableCallExpression": {
            const callee = inferExpr(node.base, env);
            const fn = unwrapFun(callee);
            if (fn && fn.returns && fn.returns[0]) return fn.returns[0];
            return primitive("any");
        }
        case "FunctionDeclaration":
            return funFromFunctionNode(node, []);
        default:
            return primitive("any");
    }
}

function unwrapFun(t) {
    if (!t) return null;
    if (t.kind === "fun") return t;
    return null;
}

function isCallNode(node) {
    return node && (
        node.type === "CallExpression" ||
        node.type === "StringCallExpression" ||
        node.type === "TableCallExpression"
    );
}

function inferReturns(node, env) {
    if (!node) return [];
    if (isCallNode(node)) {
        const fn = unwrapFun(inferExpr(node.base, env));
        if (fn && fn.returns && fn.returns.length) return fn.returns.slice();
        return [primitive("any")];
    }
    return [inferExpr(node, env)];
}

function typesForVars(variables, inits, env, tHint) {
    const types = [];
    const list = inits || [];
    list.forEach((init, i) => {
        const last = i === list.length - 1;
        if (last && isCallNode(init)) {
            inferReturns(init, env).forEach((t) => types.push(t));
        } else {
            types.push(inferExpr(init, env));
        }
    });
    while (types.length < variables.length) types.push(primitive("any"));
    if (tHint && variables.length) types[0] = tHint.type;
    return types;
}

function tableFromConstructor(node, env) {
    const fields = Object.create(null);
    (node.fields || []).forEach((f) => {
        if (f.type === "TableKeyString") {
            fields[f.key.name] = { name: f.key.name, type: inferExpr(f.value, env), doc: "" };
        }
    });
    return { kind: "table", fields: fields };
}

function fieldType(env, t, name) {
    if (!t || !name) return null;
    if (t.kind === "union") {
        for (let i = 0; i < t.types.length; i++) {
            const f = fieldType(env, t.types[i], name);
            if (f) return f;
        }
        return null;
    }
    if (t.kind === "table" && t.fields[name]) return t.fields[name].type;
    if (t.kind === "class") {
        const cls = env.classes[t.name];
        if (cls && cls.fields[name]) return cls.fields[name].type;
    }
    return null;
}

function classFields(env, t) {
    if (!t) return [];
    if (t.kind === "union") {
        const acc = [];
        t.types.forEach((x) => {
            classFields(env, x).forEach((f) => acc.push(f));
        });
        return acc;
    }
    if (t.kind === "table") {
        return Object.keys(t.fields).map((k) => t.fields[k]);
    }
    if (t.kind === "class") {
        const cls = env.classes[t.name];
        if (!cls) return [];
        return Object.keys(cls.fields).map((k) => cls.fields[k]);
    }
    return [];
}

function lookupName(env, name) {
    if (env.locals[name]) return env.locals[name];
    if (env.globals[name]) return env.globals[name];
    return null;
}

function funFromFunctionNode(node, pending) {
    const params = (node.parameters || []).map((p) => ({
        name: p.name || "...",
        type: primitive("any"),
    }));
    const returns = [];
    pending.forEach((p) => {
        if (p.kind === "param") {
            const found = params.find((x) => x.name === p.name);
            if (found) found.type = p.type;
        }
        if (p.kind === "return") returns.push(p.type);
    });
    return { kind: "fun", params, returns };
}

function functionName(node) {
    if (!node.identifier) return null;
    if (node.identifier.type === "Identifier") return node.identifier.name;
    if (node.identifier.type === "MemberExpression") {
        return node.identifier.identifier && node.identifier.identifier.name;
    }
    return null;
}

function walk(node, env, pending) {
    if (!node) return;
    const line = node.loc && node.loc.start.line;
    const annots = line ? takePendingBefore(pending, line) : [];

    switch (node.type) {
        case "Chunk":
            (node.body || []).forEach((s) => walk(s, env, pending));
            return;
        case "LocalStatement": {
            const tHint = annots.filter((a) => a.kind === "type").pop();
            const vars = node.variables || [];
            const types = typesForVars(vars, node.init, env, tHint);
            vars.forEach((v, i) => {
                const t = types[i] || primitive("any");
                bindName(env, v.name, t, true);
                env.symbols.push({
                    name: v.name,
                    kind: "variable",
                    type: t,
                    line: v.loc && v.loc.start.line,
                });
            });
            return;
        }
        case "AssignmentStatement": {
            const tHint = annots.filter((a) => a.kind === "type").pop();
            const vars = (node.variables || []).filter((v) => v.type === "Identifier");
            const types = typesForVars(vars, node.init, env, tHint);
            vars.forEach((v, i) => {
                const t = types[i] || primitive("any");
                bindName(env, v.name, t, false);
                env.symbols.push({
                    name: v.name,
                    kind: "variable",
                    type: t,
                    line: v.loc && v.loc.start.line,
                });
            });
            return;
        }
        case "FunctionDeclaration": {
            const name = functionName(node);
            const fn = funFromFunctionNode(node, annots);
            if (name) {
                if (node.isLocal) env.locals[name] = fn;
                else env.globals[name] = fn;
                env.symbols.push({
                    name: name,
                    kind: "function",
                    type: fn,
                    line: line,
                });
            }
            (node.body || []).forEach((s) => walk(s, env, pending));
            return;
        }
        default:
            Object.keys(node).forEach((k) => {
                const v = node[k];
                if (v && typeof v === "object") {
                    if (Array.isArray(v)) v.forEach((x) => {
                        if (x && x.type) walk(x, env, pending);
                    });
                    else if (v.type) walk(v, env, pending);
                }
            });
    }
}

function bindName(env, name, type, isLocal) {
    if (!name || !type) return;
    const bucket = isLocal ? env.locals : env.globals;
    const existing = bucket[name];
    if (existing && existing.kind === "class" && type.kind !== "class") return;
    bucket[name] = type;
}

function buildEnv(files, pos) {
    const env = {
        classes: Object.create(null),
        globals: Object.create(null),
        locals: Object.create(null),
        symbols: [],
    };

    files.forEach((f) => {
        const cats = parseLuaCats(f.text);
        Object.keys(cats.classes).forEach((k) => {
            env.classes[k] = cats.classes[k];
        });
        Object.keys(cats.globals).forEach((k) => {
            env.globals[k] = cats.globals[k];
        });
    });

    function walkFile(f) {
        const cats = parseLuaCats(f.text);
        const atCursor = pos && f.name === pos.file;
        const ast = atCursor ? parseLua(f.text, pos.line, pos.column) : parseLua(f.text);
        const localEnv = {
            classes: env.classes,
            globals: env.globals,
            locals: f.library ? env.globals : Object.create(null),
            symbols: env.symbols,
        };
        if (f.library) {
            localEnv.locals = env.globals;
        }
        walk(ast, localEnv, cats.pending);
        if (!f.library) {
            Object.keys(localEnv.locals).forEach((k) => {
                env.locals[k] = localEnv.locals[k];
            });
        }
    }

    files.filter((f) => f.library).forEach(walkFile);
    files.filter((f) => !f.library).forEach(walkFile);

    return env;
}

function linePrefix(text, line, column) {
    const lines = text.split("\n");
    const src = lines[line - 1] || "";
    return src.slice(0, column - 1);
}

function memberContext(prefix) {
    const m = prefix.match(/([A-Za-z_]\w*(?:\s*[.:]\s*[A-Za-z_]\w*)*)\s*([.:])\s*([A-Za-z_]\w*)?$/);
    if (!m) return null;
    const chain = m[1].replace(/\s+/g, "");
    return {
        chain: chain.split(/[.:]/),
        indexer: m[2],
        partial: m[3] || "",
    };
}

function identContext(prefix) {
    const m = prefix.match(/([A-Za-z_]\w*)$/);
    return m ? m[1] : "";
}

function resolveChain(env, parts) {
    if (!parts.length) return null;
    let t = lookupName(env, parts[0]);
    for (let i = 1; i < parts.length && t; i++) {
        t = fieldType(env, t, parts[i]);
    }
    return t;
}

function typeLabel(t) {
    if (!t) return "any";
    if (t.kind === "primitive") return t.name;
    if (t.kind === "class") return t.name;
    if (t.kind === "table") return "table";
    if (t.kind === "union") return t.types.map(typeLabel).join("|");
    if (t.kind === "fun") {
        const params = t.params.map((p) => p.name + ": " + typeLabel(p.type)).join(", ");
        const ret = t.returns.length ? ": " + t.returns.map(typeLabel).join(", ") : "";
        return "fun(" + params + ")" + ret;
    }
    return "any";
}

function funDisplay(name, fn, hideSelf) {
    const params = (fn.params || []).filter((p, i) => !(hideSelf && i === 0 && p.name === "self"));
    const paramStr = params.map((p) => p.name + ": " + typeLabel(p.type)).join(", ");
    const ret = fn.returns && fn.returns.length ? ": " + fn.returns.map(typeLabel).join(", ") : "";
    return name + "(" + paramStr + ")" + ret;
}

function itemFromField(field) {
    const isFun = field.type && field.type.kind === "fun";
    return {
        label: field.name,
        kind: isFun ? "function" : "field",
        insertText: field.name,
        detail: typeLabel(field.type),
        documentation: field.doc || "",
        type: field.type,
    };
}

function itemFromSymbol(sym) {
    const isFun = sym.type && sym.type.kind === "fun";
    return {
        label: sym.name,
        kind: isFun ? "function" : "variable",
        insertText: sym.name,
        detail: typeLabel(sym.type),
        documentation: "",
        type: sym.type,
    };
}

function matchesPrefix(name, prefix) {
    if (!prefix) return true;
    return name.toLowerCase().indexOf(prefix.toLowerCase()) === 0;
}

function userText(req) {
    const f = req.files.find((x) => x.name === req.file);
    return f ? f.text : "";
}

function complete(req) {
    const env = buildEnv(req.files, req);
    const text = userText(req);
    const prefix = linePrefix(text, req.line, req.column);
    const member = memberContext(prefix);
    const items = [];

    if (member) {
        const t = resolveChain(env, member.chain);
        classFields(env, t).forEach((f) => {
            if (matchesPrefix(f.name, member.partial)) items.push(itemFromField(f));
        });
        return { items: dedupe(items) };
    }

    const ident = identContext(prefix);
    const seen = Object.create(null);

    function addSym(name, type, kind) {
        if (!name || seen[name]) return;
        if (!matchesPrefix(name, ident)) return;
        seen[name] = true;
        items.push({
            label: name,
            kind: kind || ((type && type.kind === "fun") ? "function" : "variable"),
            insertText: name,
            detail: typeLabel(type),
            documentation: "",
            type: type,
        });
    }

    Object.keys(env.locals).forEach((n) => addSym(n, env.locals[n]));
    Object.keys(env.globals).forEach((n) => addSym(n, env.globals[n]));
    env.symbols.forEach((s) => addSym(s.name, s.type, s.kind));

    return { items: dedupe(items) };
}

function dedupe(items) {
    const seen = Object.create(null);
    return items.filter((i) => {
        if (seen[i.label]) return false;
        seen[i.label] = true;
        return true;
    });
}

function wordAt(text, line, column) {
    const src = (text.split("\n")[line - 1] || "");
    const idx = column - 1;
    const left = src.slice(0, idx);
    const right = src.slice(idx);
    const l = left.match(/[A-Za-z_]\w*$/);
    const r = right.match(/^[A-Za-z_]\w*/);
    const start = l ? l[0] : "";
    const end = r ? r[0] : "";
    const name = start + end;
    const before = left.slice(0, left.length - start.length);
    const member = memberContext(before + name);
    return { name, member, before };
}

function hover(req) {
    const env = buildEnv(req.files, req);
    const text = userText(req);
    const w = wordAt(text, req.line, req.column);
    if (!w.name) return null;

    let t = null;
    let title = w.name;
    if (w.member && w.member.chain.length) {
        const obj = resolveChain(env, w.member.chain);
        t = fieldType(env, obj, w.name) || obj;
        if (w.member.chain.length) {
            title = w.member.chain.concat([w.name]).join(w.member.indexer || ".");
        }
    } else {
        t = lookupName(env, w.name);
    }
    if (!t) return null;

    const contents = [];
    if (t.kind === "fun") {
        contents.push(funDisplay(w.name, t, true));
    } else {
        contents.push(title + ": " + typeLabel(t));
    }
    return { contents };
}

function callContext(prefix) {
    let depth = 0;
    let comma = 0;
    let paren = -1;
    for (let i = prefix.length - 1; i >= 0; i--) {
        const c = prefix[i];
        if (c === ")") depth++;
        else if (c === "(") {
            if (depth === 0) {
                paren = i;
                break;
            }
            depth--;
        } else if (c === "," && depth === 0) {
            comma++;
        }
    }
    if (paren < 0) return null;
    const before = prefix.slice(0, paren);
    const m = before.match(/([A-Za-z_]\w*(?:\s*[.:]\s*[A-Za-z_]\w*)*)\s*$/);
    if (!m) return null;
    const raw = m[1].replace(/\s+/g, "");
    const parts = raw.split(/[.:]/);
    const indexer = raw.indexOf(":") >= 0 ? ":" : ".";
    return { parts, indexer, activeParameter: comma };
}

function signatureHelp(req) {
    const env = buildEnv(req.files, req);
    const text = userText(req);
    const prefix = linePrefix(text, req.line, req.column);
    const ctx = callContext(prefix);
    if (!ctx) return { signatures: [], activeParameter: 0 };

    let fn = null;
    let name = ctx.parts[ctx.parts.length - 1];
    if (ctx.parts.length === 1) {
        fn = unwrapFun(lookupName(env, name));
    } else {
        const obj = resolveChain(env, ctx.parts.slice(0, -1));
        fn = unwrapFun(fieldType(env, obj, name));
    }
    if (!fn) return { signatures: [], activeParameter: 0 };

    const hideSelf = ctx.indexer === ":";
    const params = (fn.params || []).filter((p, i) => !(hideSelf && i === 0 && p.name === "self"));
    return {
        signatures: [{
            label: funDisplay(name, { kind: "fun", params: params, returns: fn.returns }, false),
            parameters: params.map((p) => ({ label: p.name + ": " + typeLabel(p.type) })),
        }],
        activeParameter: Math.min(ctx.activeParameter, Math.max(0, params.length - 1)),
    };
}

module.exports = {
    complete,
    hover,
    signatureHelp,
    parseType,
    parseLuaCats,
    buildEnv,
};
